/**
 * Freunde werben — an owner hands their code to another owner, and
 * both get a month of management free once the referred owner signs (COL-528).
 *
 * Two columns: what has happened on the left, what to do next on the right.
 *
 * The page never promises a credit that has not been applied. "1 Monat
 * ausstehend" and "1 Monat gutgeschrieben · Sep" are different claims, and the
 * status they hang off is decided server-side — including "Vertrag
 * unterschrieben", which is derived from the referred owner's verified
 * contract rather than from a status somebody has to remember to advance.
 */
import { useState } from "react";
import { AdminV2Layout } from "@/components/admin/v2/AdminV2Layout";
import { useLanguage } from "@/contexts/LanguageContext";
import { useOwnerReferral } from "@/hooks/useOwnerReferral";
import {
  useMyOwnerReferrals,
  useRedeemOwnerReferralCode,
  useSendReferralInvite,
  type InviteError,
  referralTotals,
  type OwnerReferralStage,
  type RedeemError,
} from "@/hooks/owner/useOwnerReferralProgramme";

const C = {
  petrol: "#0F4C4A",
  petrolDeep: "#0B3A38",
  hairline: "#ECE7DD",
  muted: "#7A8E8C",
  body: "#47605E",
  sand: "#FBF9F5",
  tint: "#E3EFED",
  clayTint: "rgba(232,168,124,0.28)",
  clayText: "#8A6B54",
} as const;

export default function OwnerReferralsPage() {
  const { t, language } = useLanguage();
  const locale = language === "de" ? "de-DE" : "en-GB";
  const { data: mine } = useOwnerReferral();
  const { data: rows = [], isError: referralsError } = useMyOwnerReferrals();
  const totals = referralTotals(rows);
  const ownCode = mine?.ownCode ?? null;

  const date = (iso: string) =>
    new Date(iso).toLocaleDateString(locale, {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });

  const stageLabel: Record<OwnerReferralStage, string> = {
    enquired: t("owner.refer.stageEnquired", "Angefragt"),
    onboarding: t("owner.refer.stageOnboarding", "Im Onboarding"),
    contract_signed: t("owner.refer.stageSigned", "Vertrag unterschrieben"),
    credited: t("owner.refer.stageCredited", "Gutgeschrieben"),
  };

  const stageTone: Record<OwnerReferralStage, { bg: string; color: string }> = {
    enquired: { bg: C.clayTint, color: C.clayText },
    onboarding: { bg: C.tint, color: C.petrol },
    contract_signed: { bg: C.tint, color: C.petrol },
    credited: { bg: C.tint, color: C.petrol },
  };

  const rewardText = (r: (typeof rows)[number]) => {
    if (r.status === "credited") {
      const month = r.reward_month
        ? new Date(`${r.reward_month}T00:00:00`).toLocaleDateString(locale, {
            month: "short",
          })
        : null;
      return month
        ? t(
            "owner.refer.rewardCreditedOn",
            "1 Monat gutgeschrieben · {m}"
          ).replace("{m}", month)
        : t("owner.refer.rewardCredited", "1 Monat gutgeschrieben");
    }
    if (r.status === "contract_signed") {
      return t(
        "owner.refer.rewardEarned",
        "1 Monat verdient – wird verrechnet"
      );
    }
    return t("owner.refer.rewardPending", "1 Monat ausstehend");
  };

  return (
    <AdminV2Layout title={t("owner.referrals.title", "Freunde werben")}>
      <div className="grid gap-10 lg:grid-cols-[minmax(0,1fr)_320px]">
        <section>
          <div
            className="grid grid-cols-2 gap-8 border-b pb-7"
            style={{ borderColor: C.hairline }}
          >
            <div>
              <p className="text-[13px]" style={{ color: C.muted }}>
                {t("owner.refer.statSuccessful", "Erfolgreiche Empfehlungen")}
              </p>
              <p
                className="mt-1.5 text-[26px] font-bold leading-none tabular-nums"
                style={{ color: C.petrol }}
              >
                {totals.successful}
              </p>
            </div>
            <div>
              <p className="text-[13px]" style={{ color: C.muted }}>
                {t("owner.refer.statMonths", "Gebührenfreie Monate")}
              </p>
              <p
                className="mt-1.5 text-[26px] font-bold leading-none tabular-nums"
                style={{ color: C.petrol }}
              >
                {totals.feeFreeMonths}
              </p>
              <p className="mt-1.5 text-xs" style={{ color: C.muted }}>
                {t("owner.refer.statMonthsHint", "bereits verdient")}
              </p>
            </div>
          </div>

          <h2
            className="mb-3 mt-7 text-[15px] font-semibold"
            style={{ color: C.petrol }}
          >
            {t("owner.refer.yourReferrals", "Ihre Empfehlungen")}
          </h2>

          {referralsError ? (
            // Not "no referrals yet": that would tell an owner nobody used
            // their code when in fact we could not find out.
            <p
              className="py-6 text-[13px]"
              style={{ color: C.clayText }}
              role="alert"
            >
              {t(
                "owner.refer.loadFailed",
                "Ihre Empfehlungen konnten nicht geladen werden. Bitte laden Sie die Seite neu."
              )}
            </p>
          ) : rows.length === 0 ? (
            <p className="py-6 text-[13px]" style={{ color: C.muted }}>
              {t(
                "owner.refer.empty",
                "Noch keine Empfehlungen. Teilen Sie Ihren Code – die Prämie gilt für jeden Eigentümer, der über Sie zu coliviq kommt."
              )}
            </p>
          ) : (
            <table className="w-full border-collapse text-[13.5px]">
              <thead>
                <tr className="text-xs" style={{ color: C.muted }}>
                  <th
                    className="border-b pb-2.5 text-left font-normal"
                    style={{ borderColor: C.hairline }}
                  >
                    {t("owner.refer.colOwner", "Empfohlener Eigentümer")}
                  </th>
                  <th
                    className="border-b pb-2.5 text-left font-normal"
                    style={{ borderColor: C.hairline }}
                  >
                    {t("owner.refer.colEntered", "Code eingegeben")}
                  </th>
                  <th
                    className="border-b pb-2.5 text-left font-normal"
                    style={{ borderColor: C.hairline }}
                  >
                    {t("owner.refer.colStatus", "Status")}
                  </th>
                  <th
                    className="border-b pb-2.5 text-right font-normal"
                    style={{ borderColor: C.hairline }}
                  >
                    {t("owner.refer.colReward", "Prämie")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr
                    key={`${r.code_used}-${r.entered_at}`}
                    className="border-b"
                    style={{ borderColor: C.hairline }}
                  >
                    <td className="py-3.5">
                      <span
                        className="block font-medium"
                        style={{ color: C.petrol }}
                      >
                        {r.referred_name ??
                          t("owner.refer.unnamed", "Eigentümer")}
                      </span>
                      <span
                        className="block text-xs"
                        style={{ color: C.muted }}
                      >
                        {r.referred_email ?? ""}
                      </span>
                    </td>
                    <td
                      className="py-3.5 tabular-nums"
                      style={{ color: C.body }}
                    >
                      {date(r.entered_at)}
                    </td>
                    <td className="py-3.5">
                      <span
                        className="inline-block rounded-full px-3 py-[5px] text-xs font-semibold"
                        style={stageTone[r.status]}
                      >
                        {stageLabel[r.status]}
                      </span>
                    </td>
                    <td className="py-3.5 text-right" style={{ color: C.body }}>
                      {rewardText(r)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>

        <aside className="flex flex-col gap-4">
          <RedeemCard claimed={mine?.claimedCode ?? null} />
          <OwnCodeCard code={ownCode} />
          {/* No code yet means nothing to invite anyone to. */}
          {ownCode && <InviteCard />}
        </aside>
      </div>
    </AdminV2Layout>
  );
}

/** Enter somebody else's code. Hidden once this owner has used one. */
function RedeemCard({ claimed }: { claimed: string | null }) {
  const { t } = useLanguage();
  const [code, setCode] = useState("");
  const redeem = useRedeemOwnerReferralCode();

  const message: Record<RedeemError, string> = {
    code_invalid: t(
      "owner.refer.errInvalid",
      "Dieser Code ist nicht gültig. Bitte prüfen Sie die Schreibweise."
    ),
    own_code: t(
      "owner.refer.errOwn",
      "Sie können Ihren eigenen Code nicht einlösen."
    ),
    already_redeemed: t(
      "owner.refer.errAlready",
      "Sie haben bereits einen Code eingelöst."
    ),
    unknown: t(
      "owner.refer.errUnknown",
      "Das hat nicht geklappt. Bitte versuchen Sie es erneut."
    ),
  };

  if (claimed) {
    return (
      <div
        className="rounded-[18px] border px-6 py-[22px]"
        style={{ borderColor: C.hairline, background: C.sand }}
      >
        <p className="text-[13.5px] font-semibold" style={{ color: C.petrol }}>
          {t("owner.refer.claimTitle", "Ihre Empfehlung")}
        </p>
        <p
          className="mt-3 inline-block rounded-full px-3 py-[5px] text-xs font-bold tracking-[0.06em] tabular-nums"
          style={{ background: C.tint, color: C.petrol }}
        >
          {claimed}
        </p>
      </div>
    );
  }

  return (
    <div
      className="rounded-[18px] border px-6 py-[22px]"
      style={{ borderColor: C.hairline, background: C.sand }}
    >
      <p className="text-[13.5px] font-semibold" style={{ color: C.petrol }}>
        {t(
          "owner.refer.redeemTitle",
          "Code eines anderen Eigentümers einlösen"
        )}
      </p>
      <p
        className="mt-2 text-[12.5px] leading-[1.55]"
        style={{ color: C.body }}
      >
        {t(
          "owner.refer.redeemBody",
          "Wurden Sie von einem coliviq-Eigentümer empfohlen? Geben Sie hier seinen Code ein. Nach Freigabe durch coliviq erhalten Sie beide 1 gebührenfreien Monat."
        )}
      </p>
      <div className="mt-3.5 flex gap-2">
        <input
          value={code}
          onChange={(e) => setCode(e.target.value)}
          placeholder={t("owner.refer.codePlaceholder", "z. B. AB-CLVQ-12")}
          aria-label={t("owner.refer.codeLabel", "Empfehlungscode")}
          className="h-10 min-w-0 flex-1 rounded-xl border px-3 text-sm uppercase outline-none"
          style={{ borderColor: C.hairline, background: "#FFFFFF" }}
        />
        <button
          type="button"
          onClick={() => redeem.mutate(code)}
          disabled={!code.trim() || redeem.isPending}
          className="h-10 shrink-0 rounded-full px-4 text-[13px] font-semibold text-white disabled:opacity-50"
          style={{ background: C.petrol }}
        >
          {t("owner.refer.redeem", "Code einlösen")}
        </button>
      </div>
      {redeem.isError && (
        <p className="mt-2 text-xs" style={{ color: C.clayText }} role="alert">
          {message[redeem.error as RedeemError] ?? message.unknown}
        </p>
      )}
    </div>
  );
}

/** The owner's own code, or why they do not have one yet. */
function OwnCodeCard({ code }: { code: string | null }) {
  const { t } = useLanguage();
  const [copied, setCopied] = useState(false);

  if (!code) {
    return (
      <div
        className="rounded-[18px] border-[1.5px] border-dashed px-6 py-[22px]"
        style={{ borderColor: "#D5DDDC" }}
      >
        <p className="text-[13.5px] font-semibold" style={{ color: C.muted }}>
          {t("owner.referrals.ownTitle", "Ihr eigener Code")}
        </p>
        <p
          className="mt-2 text-[12.5px] leading-[1.55]"
          style={{ color: C.muted }}
        >
          {t(
            "owner.refer.ownLockedBody",
            "Ihr Empfehlungscode folgt, sobald coliviq Ihre erste Immobilie freigegeben hat."
          )}
        </p>
      </div>
    );
  }

  const share = `${window.location.origin}/r/${encodeURIComponent(code)}`;

  const copy = async (value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* a browser that refuses the clipboard is not an error worth showing */
    }
  };

  return (
    <div
      className="rounded-[18px] px-6 py-[22px]"
      style={{ background: C.petrol }}
    >
      <p
        className="text-[11px] font-semibold uppercase tracking-[0.12em]"
        style={{ color: "rgba(255,255,255,0.65)" }}
      >
        {t("owner.refer.ownCode", "Ihr Empfehlungscode")}
      </p>
      <p className="mt-2 text-[26px] font-bold tracking-[0.04em] tabular-nums text-white">
        {code}
      </p>
      <div className="mt-3.5 flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => void copy(code)}
          className="h-9 rounded-full bg-white px-4 text-[13px] font-semibold"
          style={{ color: C.petrol }}
        >
          {copied
            ? t("owner.refer.copied", "Kopiert")
            : t("owner.refer.copy", "Kopieren")}
        </button>
        <button
          type="button"
          onClick={() => void copy(share)}
          className="h-9 rounded-full border px-4 text-[13px] font-semibold text-white"
          style={{ borderColor: "rgba(255,255,255,0.4)" }}
        >
          {t("owner.refer.shareLink", "Link teilen")}
        </button>
      </div>
    </div>
  );
}

/** Send the code to one address. */
function InviteCard() {
  const { t } = useLanguage();
  const [email, setEmail] = useState("");
  const invite = useSendReferralInvite();

  return (
    <div
      className="rounded-[18px] border px-6 py-[22px]"
      style={{ borderColor: C.hairline }}
    >
      <p className="text-[13.5px] font-semibold" style={{ color: C.petrol }}>
        {t("owner.refer.inviteTitle", "Per E-Mail einladen")}
      </p>
      <input
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder={t(
          "owner.refer.invitePlaceholder",
          "E-Mail-Adresse des Eigentümers"
        )}
        aria-label={t(
          "owner.refer.inviteLabel",
          "E-Mail-Adresse des Eigentümers"
        )}
        className="mt-3 h-10 w-full rounded-xl border px-3 text-sm outline-none"
        style={{ borderColor: C.hairline, background: "#FFFFFF" }}
      />
      <button
        type="button"
        onClick={() => invite.mutate(email, { onSuccess: () => setEmail("") })}
        disabled={!email.trim() || invite.isPending}
        className="mt-3 h-10 rounded-full px-4 text-[13px] font-semibold text-white disabled:opacity-50"
        style={{ background: C.petrol }}
      >
        {invite.isPending
          ? t("owner.refer.sending", "Wird gesendet…")
          : t("owner.refer.sendInvite", "Einladung senden")}
      </button>
      {invite.isSuccess && (
        <p className="mt-2 text-xs" style={{ color: C.petrol }} role="status">
          {t("owner.refer.inviteSent", "Einladung gesendet")}
        </p>
      )}
      {invite.isError && (
        <p className="mt-2 text-xs" style={{ color: C.clayText }} role="alert">
          {(invite.error as InviteError | null)?.reason === "rate_limited"
            ? t(
                "owner.refer.inviteRateLimited",
                "Sie haben heute die maximale Anzahl an Einladungen gesendet. Bitte versuchen Sie es morgen erneut."
              )
            : t(
                "owner.refer.inviteFailed",
                "Die Einladung konnte nicht gesendet werden."
              )}
        </p>
      )}
    </div>
  );
}
